import { HERO_CLASSES } from '../data/presets';
import { t } from '../../i18n';
import type { SaveData } from './model';

/**
 * Hero slots: two heroes are free; slots 3 to 12 are unlocked with coins
 * earned in play (200, 200, 300, 400, 500… one hundred more each time), and
 * slots 3 to 6 also for 19 baht each in the Special Shelf (`heroslot:N` from
 * shared/shop.ts). Players who already had more heroes keep all of them.
 */
export const FREE_HERO_SLOTS = 2;
export const MAX_HERO_SLOTS = 12;
/** Coin price of slot 3, 4, 5 … MAX_HERO_SLOTS: 200, 200, 300, 400, 500 … */
export const HERO_SLOT_PRICES = Array.from({ length: MAX_HERO_SLOTS - FREE_HERO_SLOTS }, (_, i) => Math.max(200, (i + 1) * 100));

/** Slots unlocked for real money (each `heroslot:N` counts once). */
function paidSlots(save: SaveData): number {
  return new Set(save.cosmetics.owned.filter((id) => /^heroslot:[3-6]$/.test(id))).size;
}

/** Slots unlocked (free + coins + real money), at most MAX_HERO_SLOTS. */
export function unlockedHeroSlots(save: SaveData): number {
  return Math.min(MAX_HERO_SLOTS, FREE_HERO_SLOTS + save.heroSlots + paidSlots(save));
}

/** Hero slots the player has: unlocked ones, or more when they already had more heroes. */
export function heroSlotCount(save: SaveData): number {
  return Math.max(save.heroes.length, unlockedHeroSlots(save));
}

export const canCreateHero = (save: SaveData): boolean => save.heroes.length < heroSlotCount(save);

/** The slot number that unlocks next (3–6), or null when all six are open. */
export function nextHeroSlot(save: SaveData): number | null {
  const n = unlockedHeroSlots(save);
  return n >= MAX_HERO_SLOTS ? null : n + 1;
}

/** Coin price of the next slot, or null when all are open. */
export function nextHeroSlotPrice(save: SaveData): number | null {
  const slot = nextHeroSlot(save);
  return slot === null ? null : HERO_SLOT_PRICES[slot - FREE_HERO_SLOTS - 1]!;
}

/** Unlocks the next slot with coins. */
export function buyHeroSlot(save: SaveData): boolean {
  const price = nextHeroSlotPrice(save);
  if (price === null || save.coins < price) return false;
  save.coins -= price;
  save.heroSlots += 1;
  return true;
}

/** The real-money product that unlocks the next slot (shared/shop.ts). */
export const heroSlotSku = (slot: number): string => `slot:hero${slot}`;

/* ---------- hero names ---------- */

/** A typed hero name: 1–24 characters after trimming, and not just the class's own name. */
export function heroNameProblem(name: string, classId: string): string | null {
  const n = name.trim();
  if (!n) return t('Give your hero a name first!');
  if (n.length > 24) return t('Names can be up to 24 letters.');
  if (isClassName(n, classId)) return t('That is the class name. Think of a name of your own!');
  return null;
}

/** True when a hero is still called by its class name (the old default). */
export function isClassName(name: string, classId: string): boolean {
  const cls = HERO_CLASSES.find((c) => c.id === classId);
  if (!cls) return false;
  const n = name.trim().toLowerCase();
  return n === cls.name.toLowerCase() || n === t(cls.name).toLowerCase();
}

const NAME_START_EN = ['Captain', 'Sir', 'Lady', 'Little', 'Super', 'Mighty', 'Sparkle', 'Doctor', 'Professor', 'Princess', 'Prince', 'Agent'];
const NAME_END_EN = ['Doodle', 'Crayon', 'Sketch', 'Pebble', 'Noodle', 'Muffin', 'Pickle', 'Comet', 'Bubbles', 'Waffles', 'Sprinkle', 'Pancake'];
const NAME_START_TH = ['กัปตัน', 'ท่านเซอร์', 'คุณหนู', 'น้อง', 'ซูเปอร์', 'จอมพลัง', 'เจ้าหญิง', 'เจ้าชาย', 'ด็อกเตอร์', 'สายลับ', 'อัศวิน', 'นักเวทย์'];
const NAME_END_TH = ['ดินสอ', 'สีเทียน', 'ยางลบ', 'ขนมปัง', 'มะม่วง', 'ชานม', 'โดนัท', 'ดาวหาง', 'ฟองสบู่', 'วาฟเฟิล', 'แพนเค้ก', 'คัพเค้ก'];

/** A funny random hero name in the player's language (the 🎲 button). */
export function randomHeroName(lang: 'en' | 'th', rng: () => number = Math.random): string {
  const pick = (list: string[]): string => list[Math.floor(rng() * list.length)] ?? list[0]!;
  return lang === 'th' ? `${pick(NAME_START_TH)}${pick(NAME_END_TH)}` : `${pick(NAME_START_EN)} ${pick(NAME_END_EN)}`;
}
